import { useCallback, useState } from 'react';

import { useQuery, useQueryClient } from 'react-query';

import {
  IFacialRecognition,
  IFacialRecognitionProcess,
  TOnboardingType,
} from 'shared/domain/entities';
import { makeFetchFacialRecognitionUseCase } from 'shared/domain/useCases';
import { sharedQueries } from 'shared/presentation/constants';
import { useRequestErrorHandler } from 'shared/presentation/hooks';

const DEFAULT_REFETCH_TIME_MS = 1000;

export default function useRecognitionStatus(params: {
  process: IFacialRecognitionProcess;
  onSuccess(recognition: IFacialRecognition): void;
}) {
  const [type, setType] = useState<TOnboardingType | null>(null);

  const { handleError, getErrorMessage } = useRequestErrorHandler('checkout');

  const client = useQueryClient();

  const reset = useCallback(
    (type: TOnboardingType) => {
      setType(type);
      client.invalidateQueries(
        sharedQueries.facialRecognition(params.process, type),
      );
    },
    [params.process, client],
  );

  const query = useQuery(
    sharedQueries.facialRecognition(params.process, type),
    () =>
      makeFetchFacialRecognitionUseCase().execute({
        process: params.process,
        onboardingType: type,
      }),
    {
      onSuccess: recognition => {
        if (recognition.state.type === 'APPROVED') {
          params.onSuccess(recognition);
        }
      },
      onError: error =>
        handleError(error, {
          toast: false,
          registerErrorKey: 'FACIAL_RECOGNITION_ERROR',
        }),
      refetchInterval: data => {
        if (data?.status === 'PENDING') return DEFAULT_REFETCH_TIME_MS;

        return false;
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  return {
    recognition: query.data,
    loading: query.isLoading,
    error: getErrorMessage(query.error),
    selectedOnboarding: type,
    refetch: query.refetch,
    reset,
  };
}
